import {zodResolver} from "@hookform/resolvers/zod";
import {useQueryClient} from "@tanstack/react-query";
import {Info, Loader2, X} from "lucide-react";
import {useEffect, useMemo, useRef, useState} from "react";
import {useForm, useWatch} from "react-hook-form";
import {useTranslation} from "react-i18next";

import {Alert, AlertDescription, AlertTitle} from "@/components/ui/alert";
import {Badge} from "@/components/ui/badge";
import {Button} from "@/components/ui/button";
import DatePicker from "@/components/ui/date-picker";
import {Form, FormControl, FormField, FormItem, FormLabel} from "@/components/ui/form";
import FormError from "@/components/ui/form-error";
import {Input} from "@/components/ui/input";
import {Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle} from "@/components/ui/sheet";
import {Textarea} from "@/components/ui/textarea";

import {CoordinatesRequiredGate} from "@/src/modules/core/components";
import {
  FORM_SHEET_FOOTER_CLASS,
  FORM_SHEET_ROOT_FORM_CLASS,
} from "@/src/modules/core/lib/form-sheet-layout";
import {useInvalidate, useToast} from "@/src/modules/core/hooks";
import {useCoordinatesGate} from "@/src/modules/core/hooks/use-coordinates-gate";
import {resolveGpsForSubmit} from "@/src/modules/core/lib/resolve-gps-coordinates-string";
import {markPendingActiveShiftPrimaryPropertySync} from "@/src/modules/core/lib/apply-active-shift-primary-property";
import {useStoredMe} from "@/src/modules/core/store/me";
import {moment} from "@/src/modules/core/lib";
import useNotificationPermission from "@/src/modules/notifications/hooks/use-notification-permissions";
import {officerService} from "@/src/modules/officer/services";
import {PatrolRouteCombobox} from "@/src/modules/patrol-routes/components/patrol-route-combobox";
import {PropertiesCombobox} from "@/src/modules/properties/components/properties-combobox2";
import {useAllProperties} from "@/src/modules/properties/hooks/use-all-properties";
import {Property} from "@/src/modules/properties/types";

import PresetsDuration from "../../components/shift-sheet/presets-duration";
import {ShiftPropertyAssignmentBadges} from "../../components/shift-sheet/shift-property-assignment-badges";
import {ShiftPropertyAssignmentsField} from "../../components/shift-sheet/shift-property-assignments-field";
import {ShiftTypeCombobox} from "../../components/shift-sheet/type-combobox";
import {useCreateEmergencyShift} from "../../hooks";
import {emergencyShiftSchema} from "../../schemas";
import {getPrimaryFormProperty, propertyToFormProperty, type ShiftFormProperty} from "../../lib/shift-property-form";
import {PatrolVehicleConditionDialog} from "@/src/modules/company-vehicles/components";
import {shouldPromptPatrolStartConditionCheck} from "@/src/modules/company-vehicles/lib/patrol-vehicle-condition";

import {shiftsService} from "../../services";
import {EmergencyShiftSchema, Shift, ShiftType} from "../../types";
import {SheetFormScrollBody} from "@/src/modules/help/components/sheet-form-scroll-body";

import {NextShiftWarning} from "./next-shift-warning";

interface EmergencyShiftSheet {
  open: boolean;
  onClose: () => void;
}

function getInitialValues(allowedTypes: ReturnType<typeof shiftsService.getShiftTypesAvailableForMe>) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return {
    type: allowedTypes[0] ?? null,
    properties: [] as ShiftFormProperty[],
    patrol_route: null as {id: number; name: string} | null,
    from_date: today,
    from_time: moment().format("HH:mm"),
    to_date: today,
    to_time: "",
    reason: "",
    gps_coordinates: "",
  };
}

export function EmergencyShiftSheet({open, onClose}: EmergencyShiftSheet) {
  const {t} = useTranslation("shifts");
  const queryClient = useQueryClient();
  const toast = useToast();
  const invalidateMe = useInvalidate("me");
  const invalidateShifts = useInvalidate("shifts");
  const invalidateActiveShift = useInvalidate("active-shift");
  const me = useStoredMe();
  const {requestPermission, status: notificationPermissionStatus} = useNotificationPermission();
  const [patrolCondition, setPatrolCondition] = useState<{shiftId: number} | null>(null);
  const {isPending, mutateAsync} = useCreateEmergencyShift();
  const {submitBlockedByLocation} = useCoordinatesGate(true, {active: open});
  const roleHasPropertySelectorInEmergencyShift =
    shiftsService.getRoleHasPropertySelectorInEmergencyShift();

  const allowedTypes = useMemo(
    () => shiftsService.getShiftTypesAvailableForMe(),
    [me?.role.name],
  );

  const form = useForm<EmergencyShiftSchema>({
    defaultValues: getInitialValues(allowedTypes),
    resolver: zodResolver(emergencyShiftSchema),
  });

  useEffect(() => {
    if (open) {
      form.reset(getInitialValues(allowedTypes));
    }
  }, [open, allowedTypes, form]);

  const shiftType = useWatch({control: form.control, name: "type"});
  const selectedProperties = useWatch({control: form.control, name: "properties"}) ?? [];
  const typeHasMultipleProperties = shiftType
    ? shiftsService.getIsMultiplePropertiesForType(shiftType.value)
    : false;
  const isPatrol = shiftType?.value === ShiftType.PATROL;
  const isNormal = shiftType?.value === ShiftType.NORMAL;

  const propertyFilter = useMemo(
    () => shiftsService.getPropertyFilterForShiftType(shiftType?.value),
    [shiftType?.value],
  );
  const {refetch: refetchAllProperties, isFetching: isFetchingAllProperties} = useAllProperties({
    filter: propertyFilter,
    sort: "name",
  });
  const lastAddAllCountRef = useRef(0);

  const handleAddAllProperties = async () => {
    const result = await refetchAllProperties();
    if (result.data?.data) {
      lastAddAllCountRef.current = result.data.data.length;
      form.setValue(
        "properties",
        result.data.data.map((property, index) =>
          propertyToFormProperty(property, index === 0 ? "primary" : "secondary"),
        ),
        {shouldDirty: true},
      );
    }
  };

  const isAddAllPropertiesDisabled =
    isFetchingAllProperties ||
    (lastAddAllCountRef.current > 0 &&
      selectedProperties.length >= lastAddAllCountRef.current);

  const emergencyValues = form.watch();
  const hasAllRequiredFields =
    Boolean(emergencyValues.type) &&
    Boolean(emergencyValues.from_date) &&
    Boolean(emergencyValues.to_date) &&
    Boolean(emergencyValues.from_time?.trim()) &&
    Boolean(emergencyValues.to_time?.trim()) &&
    Boolean(emergencyValues.reason?.trim()) &&
    (!roleHasPropertySelectorInEmergencyShift ||
      (isPatrol
        ? Boolean(emergencyValues.patrol_route)
        : selectedProperties.length > 0));

  const handleSubmit = async (data: EmergencyShiftSchema) => {
    try {
      const hasOverlapWithNextShift = officerService.hasOverlapWithNextShift(data);
      if (hasOverlapWithNextShift) {
        form.setError("to_date", {message: "There is an overlap with the next shift"});
        return;
      }
      const primaryProperty = getPrimaryFormProperty(data.properties);
      const gps = await resolveGpsForSubmit(
        {
          propertyCoordinates: isPatrol ? null : (primaryProperty?.gps_coordinates ?? null),
        },
        {
          captureContext: {feature: "shifts", operation: "emergency_shift_start"},
        },
      );
      if (!gps) {
        return;
      }
      const emergencyShiftData: EmergencyShiftSchema = {
        ...data,
        gps_coordinates: gps.gps_coordinates,
        gps_source: gps.gps_source,
        from_time: moment().format("HH:mm"),
      };
      await mutateAsync(emergencyShiftData);
      toast.success(t("toast.shift_started"));
      if (notificationPermissionStatus === "default") {
        void requestPermission();
      }
      invalidateMe();
      invalidateShifts();
      invalidateActiveShift();
      await queryClient.refetchQueries({queryKey: ["active-shift"]});
      const d = queryClient.getQueryData<{shift: Shift | null}>(["active-shift"]);
      if (d?.shift) {
        markPendingActiveShiftPrimaryPropertySync(d.shift.shift_id);
      }
      onClose();
      if (d?.shift && shouldPromptPatrolStartConditionCheck(d.shift)) {
        setPatrolCondition({shiftId: d.shift.shift_id});
      }
    } catch (error) {
      // already handled
    }
  };

  const isBusy = isPending;

  return (
    <>
    <Sheet open={open} askConfirmation={false} onOpenChange={onClose}>
      <SheetContent side="right" className="flex min-h-0 flex-col gap-0 overflow-hidden p-0">
        <SheetHeader>
          <SheetTitle>Create an emergency shift</SheetTitle>
        </SheetHeader>

        <Form {...form}>
          <form
            className={FORM_SHEET_ROOT_FORM_CLASS}
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <SheetFormScrollBody className="flex flex-col gap-6 px-4 pt-2 pb-4">
            <CoordinatesRequiredGate />
            <fieldset
              disabled={submitBlockedByLocation}
              className="m-0 flex flex-col gap-6 border-0 p-0"
            >
            {roleHasPropertySelectorInEmergencyShift ? (
              <>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="type"
                    render={({field, formState: {errors}}) => (
                      <FormItem>
                        <div className="flex min-h-8 items-center">
                          <FormLabel required>Type</FormLabel>
                        </div>
                        <FormControl>
                          <ShiftTypeCombobox
                            items={allowedTypes}
                            value={field.value}
                            onChange={(value) => {
                              field.onChange(value);
                              form.setValue("properties", []);
                              form.setValue("patrol_route", null);
                            }}
                          />
                        </FormControl>
                        {errors.type && <FormError error={errors.type.message} />}
                      </FormItem>
                    )}
                  />
                  {isPatrol ? (
                    <FormField
                      control={form.control}
                      name="patrol_route"
                      render={({field, formState: {errors}}) => (
                        <FormItem>
                          <div className="flex min-h-8 items-center">
                            <FormLabel required>Patrol route</FormLabel>
                          </div>
                          <FormControl>
                            <PatrolRouteCombobox
                              value={field.value ?? null}
                              onChange={(value) => field.onChange(value)}
                            />
                          </FormControl>
                          {errors.patrol_route ? (
                            <FormError error={errors.patrol_route.message} />
                          ) : null}
                        </FormItem>
                      )}
                    />
                  ) : isNormal ? (
                    <FormField
                      control={form.control}
                      name="properties"
                      render={({field, formState: {errors}}) => (
                        <FormItem>
                          <div className="flex min-h-8 items-center">
                            <FormLabel required>Property</FormLabel>
                          </div>
                          <FormControl>
                            <ShiftPropertyAssignmentsField
                              filter={propertyFilter}
                              placeholder={t("form.select_property")}
                              value={(field.value as ShiftFormProperty[]) ?? []}
                              onChange={field.onChange}
                            />
                          </FormControl>
                          {errors.properties ? <FormError error={errors.properties.message} /> : null}
                        </FormItem>
                      )}
                    />
                  ) : (
                    <FormField
                      control={form.control}
                      name="properties"
                      render={({field, formState: {errors}}) => (
                        <FormItem>
                          <div className="flex min-h-8 items-center justify-between gap-2">
                            <FormLabel required>Property</FormLabel>
                            {typeHasMultipleProperties ? (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 shrink-0 px-2.5"
                                onClick={handleAddAllProperties}
                                disabled={isAddAllPropertiesDisabled}
                              >
                                Add all
                                {isFetchingAllProperties && (
                                  <Loader2 className="ml-1 size-3 animate-spin" />
                                )}
                              </Button>
                            ) : null}
                          </div>
                          <FormControl>
                            {typeHasMultipleProperties ? (
                              <PropertiesCombobox
                                filter={propertyFilter}
                                multiple
                                placeholder={t("form.select_properties")}
                                shouldRenderSelection={false}
                                value={(field.value as unknown as Property[]) ?? null}
                                onChange={(value) =>
                                  field.onChange(
                                    (value ?? []).map((property, index) =>
                                      propertyToFormProperty(
                                        property,
                                        index === 0 ? "primary" : "secondary",
                                      ),
                                    ),
                                  )
                                }
                              />
                            ) : (
                              <PropertiesCombobox
                                filter={propertyFilter}
                                multiple={false}
                                placeholder={t("form.select_property")}
                                shouldRenderSelection={false}
                                value={(field.value[0] as unknown as Property) ?? null}
                                onChange={(value) =>
                                  field.onChange(
                                    value ? [propertyToFormProperty(value, "primary")] : [],
                                  )
                                }
                              />
                            )}
                          </FormControl>
                          {errors.properties && <FormError error={errors.properties.message} />}
                        </FormItem>
                      )}
                    />
                  )}
                </div>
                {isNormal && selectedProperties.length > 0 ? (
                  <ShiftPropertyAssignmentBadges
                    properties={selectedProperties}
                    onChange={(next) => form.setValue("properties", next, {shouldDirty: true})}
                  />
                ) : null}
                {!isPatrol && !isNormal && selectedProperties.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedProperties.map((property) => (
                      <Badge key={`emergency-selected-property-${property.id}`} variant="secondary">
                        {property.name}
                        <button
                          type="button"
                          className="ring-offset-background focus:ring-ring ml-1 rounded-full outline-none focus:ring-2 focus:ring-offset-2"
                          onClick={() => {
                            form.setValue(
                              "properties",
                              selectedProperties.filter((p) => p.id !== property.id),
                            );
                          }}
                        >
                          <X className="text-muted-foreground hover:text-foreground size-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                ) : null}
              </>
            ) : (
              <FormField
                control={form.control}
                name="type"
                render={({field, formState: {errors}}) => (
                  <FormItem>
                    <FormLabel required>Type</FormLabel>
                    <FormControl>
                      <ShiftTypeCombobox
                        items={allowedTypes}
                        value={field.value}
                        onChange={(value) => {
                          field.onChange(value);
                          form.setValue("properties", []);
                          form.setValue("patrol_route", null);
                        }}
                      />
                    </FormControl>
                    {errors.type && <FormError error={errors.type.message} />}
                  </FormItem>
                )}
              />
            )}

            <Alert variant="info">
              <Info />
              <AlertTitle>From date</AlertTitle>
              <AlertDescription>Will be automatically set to the current date</AlertDescription>
            </Alert>

            <NextShiftWarning />

            <FormLabel className="mb-2">To date</FormLabel>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="to_date"
                render={({field, formState: {errors}}) => (
                  <FormItem>
                    <FormControl>
                      <DatePicker
                        {...field}
                        disabled={{before: new Date()}}
                        selected={field.value}
                        onSelect={(date: Date | null) => {
                          field.onChange(date);
                        }}
                      />
                    </FormControl>
                    {errors.to_date && <FormError error={errors.to_date.message} />}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="to_time"
                render={({field, formState: {errors}}) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="time"
                        step="60"
                        className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
                        {...field}
                      />
                    </FormControl>
                    {errors.to_time && <FormError error={errors.to_time.message} />}
                  </FormItem>
                )}
              />
            </div>

            <PresetsDuration />

            <FormField
              control={form.control}
              name="reason"
              render={({field, formState: {errors}}) => (
                <FormItem>
                  <FormLabel className="mb-2">Reason</FormLabel>
                  <FormControl>
                    <Textarea rows={10} className="resize-none" {...field} />
                  </FormControl>
                  {errors.reason && <FormError error={errors.reason.message} />}
                </FormItem>
              )}
            />
            </fieldset>
            </SheetFormScrollBody>
            <SheetFooter className={FORM_SHEET_FOOTER_CLASS}>
              <Button
                className="w-full"
                type="submit"
                disabled={isBusy || submitBlockedByLocation || !hasAllRequiredFields}
              >
                Creat{isPending ? "ing" : "e"}
                {isPending && <Loader2 className="size-4 animate-spin" />}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
    {patrolCondition != null ? (
      <PatrolVehicleConditionDialog
        open
        onOpenChange={(o) => {
          if (!o) {
            setPatrolCondition(null);
          }
        }}
        mode="start"
        shiftId={patrolCondition.shiftId}
        onComplete={async () => {
          setPatrolCondition(null);
        }}
      />
    ) : null}
    </>
  );
}
